// Design tokens (light/dark) [02 §18]. Text colours meet WCAG 4.5:1 on their surface.
export type Theme = 'light' | 'dark';

export const TOKENS = Object.freeze({
  light: Object.freeze({
    surface: '#FFFFFF',
    surfaceMuted: '#F1F5F9',
    text: '#0F172A',
    textMuted: '#475569',
    accent: '#0369A1',
    danger: '#B91C1C',
    warning: '#92400E',
    success: '#166534',
    border: '#CBD5E1',
  }),
  dark: Object.freeze({
    surface: '#0F172A',
    surfaceMuted: '#1E293B',
    text: '#F8FAFC',
    textMuted: '#CBD5E1',
    accent: '#7DD3FC',
    danger: '#FCA5A5',
    warning: '#FCD34D',
    success: '#86EFAC',
    border: '#334155',
  }),
});

export const SPACING = Object.freeze({
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
});

/** CSS custom properties for a theme (`--skm-text`, …). */
export function cssVariables(theme: Theme): Record<string, string> {
  return Object.fromEntries(
    Object.entries(TOKENS[theme]).map(([k, v]) => [
      `--skm-${k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`,
      v,
    ]),
  );
}

function luminance(hex: string): number {
  const n = hex.replace('#', '');
  const ch = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16) / 255);
  const [r, g, b] = ch.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)) as [
    number,
    number,
    number,
  ];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #RRGGBB colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
